import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { authApi } from '@/lib/auth';
import type { CurriculumDTO } from '@amrita/shared';
import { TopBar } from '@/components/ui/TopBar';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { useMe } from '@/hooks/useMe';
import { BookMarked, Bot, Search, LogOut, Palette, Camera } from 'lucide-react';
import { closeApp, isStandalone } from '@/lib/tg';

const ROLE_LABELS: Record<string, string> = {
  admin: 'Администратор',
  consultant: 'Консультант',
  intern: 'Стажёр',
  paused: 'Пауза',
  banned: 'Заблокирован',
  guest: 'Гость',
};

export default function CabinetPage() {
  const { data: me } = useMe();
  const nav = useNavigate();
  const qc = useQueryClient();

  async function handleLogout() {
    try {
      await authApi.logout();
    } finally {
      // Чистим ВЕСЬ кэш, а не только ['me']: на общем/киоск-устройстве иначе
      // следующий залогинившийся мог бы увидеть кэш предыдущего (admin-users,
      // curriculum и т.п.) в пределах staleTime/gcTime до рефетча.
      qc.clear();
      nav('/login', { replace: true });
    }
  }

  const { data: curriculum } = useQuery<CurriculumDTO>({
    queryKey: ['curriculum'],
    queryFn: () => api.get<CurriculumDTO>('/api/training/curriculum'),
    enabled: me?.role === 'intern',
  });

  const completed = curriculum?.lessons.filter((l) => l.status === 'completed').length ?? 0;
  const total = curriculum?.lessons.length ?? 0;
  const pct = total ? Math.round((completed / total) * 100) : 0;

  return (
    <div className="safe-bottom min-h-full">
      <TopBar title="Кабинет" />
      <div className="p-4 space-y-3">
        <Card className="space-y-2">
          <div className="text-sm text-tg-hint">Имя</div>
          <div className="font-medium">{me?.firstName ?? '—'}</div>
          <div className="text-sm text-tg-hint mt-2">Username</div>
          <div className="font-medium">@{me?.tgUsername ?? '—'}</div>
          <div className="text-sm text-tg-hint mt-2">Роль</div>
          <div className="font-medium">{me?.role ? (ROLE_LABELS[me.role] ?? me.role) : '—'}</div>
          {me?.curriculumStartedAt && (
            <>
              <div className="text-sm text-tg-hint mt-2">Обучение начато</div>
              <div>{new Date(me.curriculumStartedAt).toLocaleDateString('ru-RU')}</div>
            </>
          )}
        </Card>

        {me?.role === 'intern' && curriculum && (
          <Card className="space-y-2">
            <div className="font-semibold">Прогресс курса</div>
            <div className="text-2xl font-bold">
              {completed}/{total} дней · {pct}%
            </div>
            <div className="h-2 bg-tg-secondaryBg rounded-full overflow-hidden">
              <div className="h-full bg-brand-orange" style={{ width: `${pct}%` }} />
            </div>
          </Card>
        )}

        <Card className="space-y-2">
          <div className="font-semibold mb-2">Быстрые переходы</div>
          <div className="grid grid-cols-3 gap-2">
            <Button variant="secondary" onClick={() => nav('/training/search')}>
              <Search className="w-4 h-4 mr-1" />
              Поиск
            </Button>
            <Button variant="secondary" onClick={() => nav('/training/bookmarks')}>
              <BookMarked className="w-4 h-4 mr-1" />
              Закладки
            </Button>
            <Button variant="secondary" onClick={() => nav('/ai')}>
              <Bot className="w-4 h-4 mr-1" />
              ИИ
            </Button>
          </div>
        </Card>

        {me?.role === 'consultant' && !isStandalone() && (
          <Card className="space-y-2">
            <div className="font-semibold mb-2">Студии</div>
            <div className="grid grid-cols-2 gap-2">
              <Button variant="secondary" onClick={() => nav('/content')}>
                <Palette className="w-4 h-4 mr-1" />
                Контент
              </Button>
              <Button variant="secondary" onClick={() => nav('/photo')}>
                <Camera className="w-4 h-4 mr-1" />
                Фото
              </Button>
            </div>
          </Card>
        )}

        {isStandalone() ? (
          <Button variant="secondary" className="w-full" onClick={handleLogout}>
            <LogOut className="w-4 h-4 mr-2" />
            Выйти
          </Button>
        ) : (
          <Button variant="secondary" className="w-full" onClick={closeApp}>
            <LogOut className="w-4 h-4 mr-2" />
            Закрыть приложение
          </Button>
        )}
      </div>
    </div>
  );
}
